import React, { useState } from 'react';
import { validateDiscountCode, calculateDiscountedPrice } from '../../services/discountService';

const DiscountCodeInput = ({ 
  onDiscountApplied, 
  onDiscountRemoved, 
  originalPrice, 
  disabled = false 
}) => {
  const [code, setCode] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [error, setError] = useState('');

  const handleApplyDiscount = async () => {
    if (!code.trim()) {
      setError('Please enter a discount code');
      return;
    }

    setIsValidating(true);
    setError('');

    try {
      const validation = await validateDiscountCode(code.trim());
      
      if (validation.valid) {
        const discountedPrice = calculateDiscountedPrice(
          originalPrice, 
          validation.discountAmount, 
          validation.discountType
        );
        
        const discountInfo = {
          code: code.trim().toUpperCase(),
          discount: validation.discount,
          discountAmount: validation.discountAmount,
          discountType: validation.discountType,
          originalPrice,
          discountedPrice,
          savings: originalPrice - discountedPrice
        };
        
        setAppliedDiscount(discountInfo);
        onDiscountApplied(discountInfo);
        setError('');
      } else {
        setError(validation.error);
        setAppliedDiscount(null);
      }
    } catch (error) {
      console.error('Error applying discount:', error);
      setError('Failed to validate discount code');
      setAppliedDiscount(null);
    } finally {
      setIsValidating(false);
    }
  };

  const handleRemoveDiscount = () => {
    setCode('');
    setAppliedDiscount(null);
    setError('');
    onDiscountRemoved();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleApplyDiscount();
    }
  };

  if (appliedDiscount) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center">
              <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="text-green-800 font-medium">
                Discount Applied: {appliedDiscount.code}
              </span>
            </div>
            <p className="text-green-700 text-sm mt-1">
              {appliedDiscount.discount.description}
            </p>
            <div className="text-green-800 text-sm mt-2">
              <span className="line-through">${appliedDiscount.originalPrice}</span>
              <span className="ml-2 font-bold text-lg">
                ${appliedDiscount.discountedPrice === 0 ? 'FREE' : appliedDiscount.discountedPrice.toFixed(2)}
              </span>
              {appliedDiscount.savings > 0 && (
                <span className="ml-2 text-green-600">
                  (Save ${appliedDiscount.savings.toFixed(2)})
                </span>
              )}
            </div>
          </div>
          <button
            onClick={handleRemoveDiscount}
            className="text-green-600 hover:text-green-800 text-sm underline"
            disabled={disabled}
          >
            Remove
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex space-x-2">
        <div className="flex-1">
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            onKeyPress={handleKeyPress}
            placeholder="Enter discount code"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue focus:border-transparent"
            disabled={disabled || isValidating}
          />
        </div>
        <button
          onClick={handleApplyDiscount}
          disabled={disabled || isValidating || !code.trim()}
          className="px-4 py-2 bg-apn-blue text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
        >
          {isValidating ? 'Validating...' : 'Apply'}
        </button>
      </div>
      
      {error && (
        <div className="text-red-600 text-sm flex items-center">
          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          {error}
        </div>
      )}
      
      <div className="text-xs text-gray-500">
        <p>Have a discount code? Enter it above to apply to your membership.</p>
      </div>
    </div>
  );
};

export default DiscountCodeInput;
